/**
 * Image URL Utility for Admin Panel
 * Handles image URL generation for different sources
 */

const PUBLIC_ASSETS_BASE_URL = import.meta.env.VITE_PUBLIC_ASSETS_BASE_URL || '';

export interface ImageData {
  // AT Protocol blob URL (for completed posts)
  blobUrl?: string;

  // Object Storage key (for pending posts)
  key?: string;

  // Legacy URL fields
  url?: string;
  image?: {
    url?: string;
    ref?: {
      $link?: string;
    };
  };

  // AT Protocol BlobRef format
  $type?: 'blob';
  ref?: {
    $link: string;
  };
  mimeType?: string;
  size?: number;

  // Legacy thumbUrl field (for external links)
  thumbUrl?: string;

  alt?: string;

  // Actor DID (for CDN URL generation)
  actorDid?: string;
}

/**
 * Generate image URL from image data
 */
export function generateImageUrl(imageData: ImageData): string {
  // 1. AT Protocol blob URL (highest priority for completed posts)
  if (imageData.blobUrl) {
    return imageData.blobUrl;
  }

  // 2. Object Storage URL (for pending posts) - check before BlobRef
  // Handles temp/ and usercontent/ paths that should use R2, not cdn.bsky.app
  if (imageData.key && PUBLIC_ASSETS_BASE_URL) {
    const baseUrl = PUBLIC_ASSETS_BASE_URL.replace(/\/$/, '');
    const key = imageData.key.replace(/^\//, '');
    return `${baseUrl}/${key}`;
  }

  // 3. AT Protocol BlobRef and legacy image.ref formats
  const link = (imageData.$type === 'blob' && imageData.ref?.$link) || imageData.image?.ref?.$link;

  if (link) {
    // If link is a storage path (temp/ or usercontent/), use R2 storage
    if ((link.startsWith('temp/') || link.startsWith('usercontent/')) && PUBLIC_ASSETS_BASE_URL) {
      const baseUrl = PUBLIC_ASSETS_BASE_URL.replace(/\/$/, '');
      const sanitizedKey = link.replace(/^\//, '');
      return `${baseUrl}/${sanitizedKey}`;
    }

    // If link is a CID (posted to Bluesky), use Bluesky CDN
    if (imageData.actorDid) {
      return `https://cdn.bsky.app/img/feed_thumbnail/plain/${imageData.actorDid}/${link}@jpeg`;
    }
  }

  // 5. ThumbUrl field - check if it's an R2 cache key or external URL
  if (imageData.thumbUrl) {
    // If thumbUrl is an R2 cache key (starts with 'ogp-cache/'), treat it as a storage key
    if (imageData.thumbUrl.startsWith('ogp-cache/') && PUBLIC_ASSETS_BASE_URL) {
      const baseUrl = PUBLIC_ASSETS_BASE_URL.replace(/\/$/, '');
      const key = imageData.thumbUrl.replace(/^\//, '');
      return `${baseUrl}/${key}`;
    }

    // Otherwise, return as-is (external URL)
    return imageData.thumbUrl;
  }

  // 6. Legacy URL fields (fallback)
  if (imageData.image?.url) {
    return imageData.image.url;
  }

  if (imageData.url) {
    return imageData.url;
  }

  // 7. If no URL found, return empty string
  console.warn('[ImageURL] No valid image URL found:', imageData);
  return '';
}
